import { IParameterizable } from '../../interfaces/IParameterizable';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { A365Named } from '../base/a365Named';
import { Collection, IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Owned } from '../base/named';
import { OwnedSet } from '../base/ownedset';
import { Parameter } from '../base/parameter';
import { Folder } from '../folder/folder';
import { LinkedService } from '../linkedservice/linkedservice';
import { ModelConstants } from '../model-constants';
import { ITable } from '../table/interface';
import { IMappingArtifactSource } from './IMappingArtifactSource';
import { MappingDirective } from './mappingdirective';
import { IRelationship } from '../relationship/interface';
import { ActionConstants } from '../../constants/action-constants';
import { validateMappingDirectivesInternal, IViolationDetail, ViolationType } from './mappingartifact-validation.util';

/* Things the ETL Mapping entity needs to keep track of:
Sources[] - all the source tables that are available for mapping in each target table
Sink[] - all of the target tables that will be mapped to

MappingDirectives: MappingDirective[] - each target table should have a MappingDirective

MappingDirective {
    targetEntityReference - name of the target table
    sourceEntityReferences[] - names of the source tables used in the mapping
    sourceDenormalizeInfo - I think this is for the joins and things to create a new source. Still a bit unclear
    attributeMappingInfo - an object with a field called attributeMappings: AttributeMapping[] which stores the info for a given column about how it is being mapped from the source to the target
}
*/
@Metamodel.model({
    name: ModelConstants.dataMapper,
    display: [shellStringResources.MappingArtifact, shellStringResources.MappingArtifacts],
    reusable: true,
    referable: true,
    validator: DataMapper.validate,
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.dataMappersWrite
        },
        deleteResource: {
            action: ActionConstants.dataMappersDelete
        }
    }
})
export class DataMapper extends A365Named implements IParameterizable {

    public static readonly MODEL: IModel;

    public static readonly MAPPING_DIRECTIVE: IAttribute;

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true
        }
    })
    public readonly parameters = OwnedSet.create<Parameter>(this);

    public folder: Folder = Owned.create(Folder.MODEL, this);

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public sourceLinkedService: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public targetLinkedService: LinkedService;

    public sourceTables: ITable[] = [];
    public sourceDatasets: IMappingArtifactSource[] = [];

    public targetTables: ITable[] = [];
    public targetDatasets: IMappingArtifactSource[] = [];

    public targetRelationships: IRelationship[] = [];

    @Metamodel.attribute({
        reference: true,
        type: MappingDirective.MODEL,
        association: 'MAPPING_DIRECTIVE',
        constraint: {
            validator: DataMapper.validateMappingDirectives
        }
    })
    public mappingDirectives: MappingDirective[] = [];

    constructor(name?: string, description?: string) {
        super(name, description, null);
        this.resourceType = ModelConstants.dataMapper;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static validateMappingDirectives(owner: any): IViolation[] | undefined {
        const violations: IViolation[] = [];
        const dm = (owner && owner.named) as DataMapper;

        dm.mappingDirectives.forEach((mappingDirective) => {
            const mappingViolations = validateMappingDirectivesInternal(mappingDirective);
            if (mappingViolations && mappingViolations.length > 0) {
                mappingViolations.forEach((arr: IViolationDetail) => {
                    const context = {
                                field: arr.violationType,
                                index: arr.index,
                                value: mappingDirective.targetEntityReference.name,
                                mappingMethod: arr.mappingMethod
                    };
                    if (arr.violationType === ViolationType.MappingMethod) {
                        violations.push({
                            context,
                            message: () => `${mappingDirective.targetEntityReference.name} - ${shellStringResources.InvalidMappingType} on ${arr.targetAttribute}.`
                        });
                    } else if (arr.violationType === ViolationType.SourceColumn) {
                        violations.push({
                            context,
                            message: () => `${mappingDirective.targetEntityReference.name} - ${shellStringResources.InvalidSourceColumn} on ${arr.targetAttribute}.`
                        });
                    } else if (arr.violationType === ViolationType.TargetColumn) {
                        violations.push({
                            context,
                            message: () => `${mappingDirective.targetEntityReference.name} - ${shellStringResources.InvalidTargetColumn}.`
                        });
                    }
                });
            }
        });

        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static validate(owner: any): IViolation[] | undefined {
            const violations: IViolation[] = [];
            const dm = (owner && owner.named) as DataMapper;
            const containsValidMappingDirectives = dm.mappingDirectives.some((directive) => {
                return directive.attributeMappingInfo.length > 0;
            });

            if (!containsValidMappingDirectives) {
                violations.push({
                    message: () => `${shellStringResources.InvalidMappings}.`
                });

                return violations;
            }

            return violations;
        }
}
